import React, {useEffect} from 'react'
import PropTypes from 'prop-types'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import {Gantt, ViewMode} from 'gantt-task-react'
import {ViewSwitcher} from './ViewSwitcher.jsx'
import {getStartEndDateForProject, transformImportedTasks, transformMultipleProjects,} from './helper'

const LIST_COL_WIDTHS = {
    name: '360px',
    date: '120px',
    budget: '160px',
}

const LIST_TOTAL_WIDTH = '760px'

const formatNumberWithSeparators = (amount) => {
    const num = parseFloat(amount)
    if (isNaN(num)) return '0.00'

    let formatted = num.toLocaleString('pl-PL', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
        useGrouping: true,
    })

    formatted = formatted.replace(',', '.')
    formatted = formatted.replace(/\u00a0/g, ' ')

    return formatted
}

/* eslint-disable react/prop-types */
const GanttChart = (props) => {
    const { tasks: initTasksList, projects } = props
    const [view, setView] = React.useState(ViewMode.Month)
    const initialTasks = React.useMemo(() => {
        return projects
            ? transformMultipleProjects(projects)
            : transformImportedTasks(initTasksList)
    }, [projects, initTasksList]);
    const [tasks, setTasks] = React.useState(initialTasks);
    const [isChecked, setIsChecked] = React.useState(true)
    let columnWidth = 30
    if (view === ViewMode.Month) {
        columnWidth = 300
    } else if (view === ViewMode.Year) {
        columnWidth = 500
    }

    useEffect(() => {
        setTasks(initialTasks);
    }, [initialTasks]);

    const TaskListHeaderPL = ({
        headerHeight,
        fontFamily,
        fontSize,
    }) => {

        return (
            <div
                style={{
                    height: headerHeight,
                    fontFamily: fontFamily,
                    fontSize: fontSize,
                    display: 'flex',
                    alignItems: 'center',
                    borderBottom: '2px solid #dee2e6',
                    backgroundColor: '#f8f9fa',
                    paddingTop: '4px',
                    paddingBottom: '4px',
                }}
            >
                <div
                    style={{
                        minWidth: LIST_COL_WIDTHS.name,
                        maxWidth: LIST_COL_WIDTHS.name,
                        width: LIST_COL_WIDTHS.name,
                        textAlign: 'left',
                        paddingLeft: '10px',
                        color: '#495057',
                        fontWeight: 'bold',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                >
                    Nazwa
                </div>
                <div
                    style={{
                        minWidth: LIST_COL_WIDTHS.date,
                        maxWidth: LIST_COL_WIDTHS.date,
                        width: LIST_COL_WIDTHS.date,
                        textAlign: 'center',
                        color: '#495057',
                        fontWeight: 'bold',
                    }}
                >
                    Od
                </div>
                <div
                    style={{
                        minWidth: LIST_COL_WIDTHS.date,
                        maxWidth: LIST_COL_WIDTHS.date,
                        width: LIST_COL_WIDTHS.date,
                        textAlign: 'center',
                        color: '#495057',
                        fontWeight: 'bold',
                    }}
                >
                    Do
                </div>

                <div
                    style={{
                        minWidth: LIST_COL_WIDTHS.budget,
                        maxWidth: LIST_COL_WIDTHS.budget,
                        width: LIST_COL_WIDTHS.budget,
                        textAlign: 'center',
                        color: '#495057',
                        fontWeight: 'bold',
                    }}
                >
                    Budżet planowany
                </div>
            </div>
        )
    }
    TaskListHeaderPL.propTypes = {
        headerHeight: PropTypes.number,
        rowWidth: PropTypes.string,
        fontFamily: PropTypes.string,
        fontSize: PropTypes.string,
    }

    const TooltipContentPL = ({ task }) => {
        const formatDate = (date) => {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        }

        const getDuration = () => {
            const duration = Math.ceil(
                (task.end.getTime() - task.start.getTime()) /
                    (1000 * 60 * 60 * 24)
            )
            const daysLabel = duration === 1 ? 'dzień' : 'dni'
            return `${duration} ${daysLabel}`
        }

        const isMilestone = task.type === 'milestone'

        return (
            <div
                style={{
                    padding: '12px',
                    backgroundColor: '#212529',
                    color: 'white',
                    borderRadius: '6px',
                    fontSize: '13px',
                    boxShadow: '0 0.5rem 1rem rgba(0, 0, 0, 0.15)',
                    minWidth: '200px',
                }}
            >
                <div
                    style={{
                        fontWeight: 'bold',
                        marginBottom: '8px',
                        fontSize: '14px',
                    }}
                >
                    {task.originalName || task.name}
                </div>
                {isMilestone ? (
                    <div style={{ marginBottom: '4px' }}>
                        <strong>Data kamienia milowego:</strong> {formatDate(task.start)}
                    </div>
                ) : (
                    <>
                        <div style={{ marginBottom: '4px' }}>
                            <strong>Data rozpoczęcia:</strong> {formatDate(task.start)}
                        </div>
                        <div style={{ marginBottom: '4px' }}>
                            <strong>Data zakończenia:</strong> {formatDate(task.end)}
                        </div>
                    </>
                )}
                {!isMilestone && (
                    <div>
                        <strong>Budżet planowany:</strong> {formatNumberWithSeparators(task.budget)}
                    </div>
                )}
                {!isMilestone && (
                <div>
                    <strong>Czas trwania:</strong> {getDuration()}
                </div>
                )}
            </div>
        )
    }
    TooltipContentPL.propTypes = {
        task: PropTypes.shape({
            id: PropTypes.string,
            name: PropTypes.string,
            originalName: PropTypes.string,
            start: PropTypes.instanceOf(Date),
            end: PropTypes.instanceOf(Date),
            budget: PropTypes.number,
            type: PropTypes.string,
            project: PropTypes.string,
            hideChildren: PropTypes.bool,
        }),
    }

    const TaskListTablePL = ({
        rowHeight,
        tasks,
        fontFamily,
        fontSize,
        onExpanderClick,
    }) => {
        const formatDate = (date) => {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        }

        return (
            <div
                style={{
                    fontFamily: fontFamily,
                    fontSize: fontSize,
                }}
            >
                {tasks.map((task) => {
                    const taskDisplayName = task.originalName || task.name
                    return (
                        <div
                            key={task.id}
                            style={{
                                height: rowHeight,
                                display: 'flex',
                                alignItems: 'center',
                                borderBottom: '1px solid #dee2e6',
                                backgroundColor:
                                    task.type === 'project'
                                        ? '#f8f9fa'
                                        : 'white',
                            }}
                        >
                            <div
                                style={{
                                    minWidth: LIST_COL_WIDTHS.name,
                                    maxWidth: LIST_COL_WIDTHS.name,
                                    width: LIST_COL_WIDTHS.name,
                                    flex: `0 0 ${LIST_COL_WIDTHS.name}`,
                                    paddingLeft:
                                        task.type === 'project' ? 10 : 30,
                                    display: 'flex',
                                    alignItems: 'center',
                                    textAlign: 'left',
                                    overflow: 'hidden',
                                }}
                            >
                                {task.type === 'project' && (
                                    <span
                                        style={{
                                            cursor: 'pointer',
                                            marginRight: 5,
                                            userSelect: 'none',
                                        }}
                                        onClick={() => onExpanderClick(task)}
                                    >
                                        {task.hideChildren ? '▶' : '▼'}
                                    </span>
                                )}
                                <OverlayTrigger
                                    placement="right"
                                    overlay={<Tooltip id={`task-name-${task.id}`}>{taskDisplayName}</Tooltip>}
                                >
                                    <span
                                        style={{
                                            display: 'block',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            width: '100%',
                                            fontWeight:
                                                task.type === 'project'
                                                    ? 'bold'
                                                    : 'normal',
                                        }}
                                    >
                                        {taskDisplayName}
                                    </span>
                                </OverlayTrigger>
                            </div>
                            <div
                                style={{
                                    minWidth: LIST_COL_WIDTHS.date,
                                    maxWidth: LIST_COL_WIDTHS.date,
                                    width: LIST_COL_WIDTHS.date,
                                    textAlign: 'center',
                                }}
                            >
                                {formatDate(task.start)}
                            </div>
                            <div
                                style={{
                                    minWidth: LIST_COL_WIDTHS.date,
                                    maxWidth: LIST_COL_WIDTHS.date,
                                    width: LIST_COL_WIDTHS.date,
                                    textAlign: 'center',
                                }}
                            >
                                {formatDate(task.end)}
                            </div>
                            <div
                                style={{
                                    minWidth: LIST_COL_WIDTHS.budget,
                                    maxWidth: LIST_COL_WIDTHS.budget,
                                    width: LIST_COL_WIDTHS.budget,
                                    textAlign: 'right',
                                    paddingRight: 10,
                                }}
                            >
                                {formatNumberWithSeparators(task.budget)}
                            </div>
                        </div>
                    )
                })}
            </div>
        )
    }
    TaskListTablePL.propTypes = {
        rowHeight: PropTypes.number,
        rowWidth: PropTypes.string,
        tasks: PropTypes.arrayOf(
            PropTypes.shape({
                id: PropTypes.string.isRequired,
                name: PropTypes.string.isRequired,
                start: PropTypes.instanceOf(Date).isRequired,
                end: PropTypes.instanceOf(Date).isRequired,
                type: PropTypes.string.isRequired,
                project: PropTypes.string,
                hideChildren: PropTypes.bool,
                budget: PropTypes.number,
            })
        ).isRequired,
        fontFamily: PropTypes.string,
        fontSize: PropTypes.string,
        onExpanderClick: PropTypes.func.isRequired,
    }

    const handleTaskChange = (task) => {
        let newTasks = tasks.map((t) => (t.id === task.id ? task : t))
        if (task.project) {
            const [start, end] = getStartEndDateForProject(
                newTasks,
                task.project
            )
            const project =
                newTasks[newTasks.findIndex((t) => t.id === task.project)]
            if (
                project.start.getTime() !== start.getTime() ||
                project.end.getTime() !== end.getTime()
            ) {
                const changedProject = { ...project, start, end }
                newTasks = newTasks.map((t) =>
                    t.id === task.project ? changedProject : t
                )
            }
        }
        setTasks(newTasks)
    }
    const handleTaskDelete = (task) => {
        const conf = window.confirm('Czy na pewno usunąć ' + task.name + '?')
        if (conf) {
            setTasks(tasks.filter((t) => t.id !== task.id))
        }
        return conf
    }
    const handleProgressChange = async (task) => {
        setTasks(tasks.map((t) => (t.id === task.id ? task : t)))
    }
    const handleDblClick = (task) => {
        alert('Kliknięto dwukrotnie zadanie: ' + task.name)
    }
    const handleSelect = (task, isSelected) => {
    }
    const handleExpanderClick = (task) => {
        setTasks(tasks.map((t) => (t.id === task.id ? task : t)))
    }

    const isTasksEmpty = !tasks || tasks.length === 0;
    const projectTask = !isTasksEmpty ? tasks.find((task) => task.type === 'project') : null
    const chartStartDate = projectTask
        ? new Date(projectTask.start.getFullYear(), projectTask.start.getMonth(), 1)
        : new Date(new Date().getFullYear(), new Date().getMonth(), 1);

    if (isTasksEmpty) {
        return (
            <div className='mt-5'>
                <h4 className='text-start mb-3'>Wykres zadań</h4>
                <div className='border rounded bg-white shadow-sm p-3'>
                    <p className='text-center text-muted m-0 p-3'>Brak zdefiniowanych zadań lub planu harmonogramu do wyświetlenia.</p>
                </div>
            </div>
        );
    }

    return (
        <div className='mt-5'>
            <ViewSwitcher
                onViewModeChange={(viewMode) => setView(viewMode)}
                onViewListChange={setIsChecked}
                isChecked={isChecked}
                currentView={view}
            />
            <h4 className='text-start mb-3'>Wykres zadań</h4>
            <div className='border rounded bg-white shadow-sm p-3'>
                <Gantt
                    tasks={tasks}
                    viewMode={view}
                    startDate={chartStartDate}
                    ganttBoundaries={'none'}
                    preStepsCount={0}
                    //onDateChange={handleTaskChange}
                    onDelete={handleTaskDelete}
                    //onProgressChange={handleProgressChange}
                    //onDoubleClick={handleDblClick}
                    onSelect={handleSelect}
                    onExpanderClick={handleExpanderClick}
                    listCellWidth={isChecked ? LIST_TOTAL_WIDTH : ''}
                    columnWidth={columnWidth}
                    barBackgroundColor='#0d6efd'
                    barBackgroundSelectedColor='#0a58ca'
                    barProgressColor='#0d6efd'
                    barProgressSelectedColor='#0a58ca'
                    projectBackgroundColor='#6c757d'
                    projectBackgroundSelectedColor='#5c636a'
                    projectProgressColor='#6c757d'
                    projectProgressSelectedColor='#5c636a'
                    milestoneBackgroundColor='#198754'
                    milestoneBackgroundSelectedColor='#157347'
                    rowHeight={45}
                    barCornerRadius={4}
                    locale='pl-PL'
                    fontSize={13}
                    TaskListHeader={TaskListHeaderPL}
                    TaskListTable={TaskListTablePL}
                    TooltipContent={TooltipContentPL}
                    ganttHeight={600}
                />
            </div>
        </div>
    )
}

GanttChart.propTypes = {
    tasks: PropTypes.array,
    projects: PropTypes.arrayOf(
        PropTypes.shape({
            tasks: PropTypes.array,
            projectId: PropTypes.string,
            projectName: PropTypes.string,
        })
    ),
}

export default GanttChart